import { useState } from 'react'
import { useBackdropDismiss } from '../hooks/useBackdropDismiss'
import { useAuth } from '../contexts/AuthContext'
import { getAuthErrorMessage } from '../utils/errorHandler'
import './EmailLoginModal.css'

function EmailLoginModal({ isOpen, onClose }) {
  const { signInWithEmailPassword, registerWithEmailPassword, sendPasswordResetEmail } = useAuth()
  const [mode, setMode] = useState('signin') // 'signin' | 'register' | 'reset'
  const [resetSent, setResetSent] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const resetForm = () => {
    setError(null)
    setEmail('')
    setPassword('')
    setDisplayName('')
    setResetSent(false)
  }

  const handleClose = () => {
    resetForm()
    setMode('signin')
    onClose()
  }

  const backdropDismiss = useBackdropDismiss(handleClose)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    if (!email.trim()) {
      setError('Veuillez saisir votre email.')
      return
    }
    if (mode === 'reset') {
      setLoading(true)
      try {
        await sendPasswordResetEmail(email)
        setResetSent(true)
      } catch (err) {
        console.error('Erreur réinitialisation mot de passe:', err)
        setError(getAuthErrorMessage(err))
      } finally {
        setLoading(false)
      }
      return
    }
    if (!password) {
      setError('Veuillez saisir votre mot de passe.')
      return
    }
    if (mode === 'register' && password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.')
      return
    }
    setLoading(true)
    try {
      if (mode === 'signin') {
        await signInWithEmailPassword(email, password)
      } else {
        await registerWithEmailPassword(email, password, displayName || null)
      }
      handleClose()
    } catch (err) {
      console.error('Erreur auth email:', err)
      setError(getAuthErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  if (mode === 'reset' && resetSent) {
    return (
      <div className="email-login-backdrop" {...backdropDismiss}>
        <div className="email-login-modal" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="email-login-close" onClick={handleClose} aria-label="Fermer">×</button>
          <h2 className="email-login-title">Vérifie ta boîte mail</h2>
          <p className="email-login-success-msg">
            Si un compte existe avec cette adresse, un lien pour réinitialiser ton mot de passe vient d’être envoyé.
          </p>
          <button
            type="button"
            className="email-login-submit"
            onClick={() => {
              setMode('signin')
              setResetSent(false)
              setError(null)
            }}
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="email-login-backdrop" {...backdropDismiss}>
      <div className="email-login-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="email-login-close" onClick={handleClose} aria-label="Fermer">×</button>
        <h2 className="email-login-title">
          {mode === 'signin' && 'Se connecter avec email'}
          {mode === 'register' && 'Créer un compte'}
          {mode === 'reset' && 'Mot de passe oublié'}
        </h2>
        <form onSubmit={handleSubmit} className="email-login-form">
          {error && (
            <div className="email-login-error" role="alert">
              {error}
            </div>
          )}
          <label className="email-login-label">
            Email
            <input
              type="email"
              className="email-login-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.ch"
              autoComplete="email"
              disabled={loading}
            />
          </label>
          {mode !== 'reset' && (
            <label className="email-login-label">
              Mot de passe
              <input
                type="password"
                className="email-login-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'register' ? 'Au moins 6 caractères' : ''}
                autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                disabled={loading}
              />
            </label>
          )}
          {mode === 'signin' && (
            <p className="email-login-forgot-wrap">
              <button
                type="button"
                className="email-login-link"
                onClick={() => {
                  setMode('reset')
                  setError(null)
                  setResetSent(false)
                }}
              >
                Mot de passe oublié ?
              </button>
            </p>
          )}
          {mode === 'register' && (
            <label className="email-login-label">
              Nom d'affichage (optionnel)
              <input
                type="text"
                className="email-login-input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Votre nom"
                autoComplete="name"
                disabled={loading}
              />
            </label>
          )}
          <button type="submit" className="email-login-submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-small"></span>
                {mode === 'signin' && 'Connexion...'}
                {mode === 'register' && 'Création...'}
                {mode === 'reset' && 'Envoi...'}
              </>
            ) : mode === 'signin' ? (
              'Se connecter'
            ) : mode === 'register' ? (
              'Créer mon compte'
            ) : (
              'Envoyer le lien'
            )}
          </button>
        </form>
        <p className="email-login-switch">
          {mode === 'signin' && (
            <>
              Pas encore de compte ?{' '}
              <button type="button" className="email-login-link" onClick={() => { setMode('register'); setError(null); }}>
                Créer un compte
              </button>
            </>
          )}
          {mode === 'register' && (
            <>
              Déjà un compte ?{' '}
              <button type="button" className="email-login-link" onClick={() => { setMode('signin'); setError(null); }}>
                Se connecter
              </button>
            </>
          )}
          {mode === 'reset' && (
            <button
              type="button"
              className="email-login-link"
              onClick={() => {
                setMode('signin')
                setError(null)
              }}
            >
              Retour à la connexion
            </button>
          )}
        </p>
      </div>
    </div>
  )
}

export default EmailLoginModal
